import { useMemo } from "react"
import { Autocomplete, Stack, TextField } from "@mui/material"
import { If } from "lib/@components/switch"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { getClientGroups } from "routes/schedule/my-schedules/my-custom-schedules/controller/get-client-trees"
import { RegimeAssignmentBoard } from "./regime-assignment-board"
import { isSameRegimeId } from "./same-regime-id"
import { AssignmentScopeNote } from "./assignment-scope-note"

/**
 * FIQ-2457: the Schedule assignments view of a skill map. Assignments belong to the skill
 * map, so the regime chosen here is only the lens used to browse schedules; the same
 * assignments apply to every regime costed with this skill map. Plugged into
 * SkillMapViewModes behind the flag; the skill map view binds what it reads.
 */
export function ScheduleAssignmentsPanel() {
    const { skillMapId, skillMapName, trades, lines, readOnly, onAddTrade, onEditTrade } = useBoundContext()
    const clientGroups = getClientGroups.useResults()
    const regimes = useMemo(buildRegimeOptions, [clientGroups])
    const [regimeId, setRegimeId] = useSearchParam("assignmentRegime", "")
    const selectedRegime = regimes.find(isSelectedRegime) ?? null

    return (
        <Stack spacing={1.5} data-cy="schedule-assignments-panel">
            {/* The picker and what is being viewed and edited share one row on wide screens, so
                the board starts high on the page; the progress line below says how much is
                assigned and that the rest uses skill routing */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1, md: 3 }} alignItems={{ md: "center" }}>
                <Autocomplete
                    data-cy="assignment-regime-selector"
                    options={regimes}
                    value={selectedRegime}
                    onChange={handleRegimeChange}
                    getOptionLabel={getRegimeLabel}
                    isOptionEqualToValue={isSameRegime}
                    renderInput={renderRegimeInput}
                    size="small"
                    sx={{ width: { xs: 1, md: 360 }, flexShrink: 0 }}
                />
                {/* The regime is only what is being looked at; edits belong to the skill map */}
                <AssignmentScopeNote regimeLabel={selectedRegime?.label} skillMapName={skillMapName} />
            </Stack>
            <If value={!!selectedRegime}>
                <RegimeAssignmentBoard
                    // A new board per skill map and regime, so neither's data shows while the other loads
                    key={`${skillMapId}|${selectedRegime?.id}`}
                    regimeId={selectedRegime?.id}
                    skillMapId={skillMapId}
                    trades={trades}
                    lines={lines}
                    readOnly={readOnly}
                    onAddTrade={onAddTrade}
                    onEditTrade={onEditTrade}
                />
            </If>
        </Stack>
    )

    // The id can come from a scenario link, stored with or without the namespace suffix
    function isSelectedRegime(regime) {
        return isSameRegimeId(regime.id, regimeId)
    }

    function handleRegimeChange(__, value) {
        setRegimeId(value?.id ?? "")
    }

    // Same regime ids the scenario picker uses ("live|<id>" / "ready|<id>")
    function buildRegimeOptions() {
        const options = []
        for (const group of clientGroups ?? []) {
            if (group.hasLive) options.push({ id: `live|${group._id}`, label: group.name })
            if (group.hasReady) options.push({ id: `ready|${group._id}`, label: `${group.name} (Ready)` })
        }
        return options.sort(byLabel)
    }
}

function getRegimeLabel(regime) {
    return regime?.label ?? ""
}

function isSameRegime(option, value) {
    return option?.id === value?.id
}

function renderRegimeInput(params) {
    return <TextField {...params} label="Browse a regime" placeholder="Choose a regime to see its schedules" />
}

function byLabel(a, b) {
    return a.label.localeCompare(b.label)
}
